import React, { forwardRef } from "react";
import { Box, Text, UnstyledButton } from "@mantine/core";
import { IconChevronDown, IconX } from "@tabler/icons-react";
import styles from "./FilterChip.module.css";

export type FilterChipVariant = "token" | "outline";

export interface FilterChipProps extends Omit<
  React.HTMLAttributes<HTMLElement>,
  "children"
> {
  /** Field name shown first, e.g. "Staff". */
  label?: React.ReactNode;
  /** Current value, e.g. "All" / "Ana Lee". Rendered bold. */
  value?: React.ReactNode;
  /** Muted trailing text, e.g. the resolved dates of a range preset. */
  hint?: React.ReactNode;
  /** Leading icon (e.g. a calendar on the date chip). */
  icon?: React.ReactNode;
  /**
   * "token" (default): the Sales applied-filter pill, unchanged.
   * "outline": the Reports toolbar chip (white at rest, tinted when active).
   */
  variant?: FilterChipVariant;
  /** Applied / non-default state (tints the "outline" chip; the "token"
   *  chip always reads as applied). */
  active?: boolean;
  /** Opt-in: ellipsize the value past this width (px number or CSS length). */
  valueMaxWidth?: number | string;
  /** Show the trailing chevron. Defaults to true unless `onRemove` is set. */
  withChevron?: boolean;
  /** Renders a ✕ that clears the filter. */
  onRemove?: () => void;
  /** Accessible name for the ✕ button. */
  removeLabel?: string;
}

/**
 * A pill-shaped toolbar filter chip: `label value hint ▾`. Forwards its ref and
 * any extra props (onClick, aria-expanded, …) to the root so it can be used
 * directly as a Mantine `Popover.Target` / `Menu.Target`.
 *
 * With `onRemove`, the root becomes a keyboard-operable `role="button"` box
 * (a real <button> can't nest the ✕ button); otherwise it is a plain button.
 */
const FilterChip = forwardRef<HTMLElement, FilterChipProps>(function FilterChip(
  {
    label,
    value,
    hint,
    icon,
    variant = "token",
    active = false,
    valueMaxWidth,
    withChevron,
    onRemove,
    removeLabel,
    className,
    style,
    onClick,
    onKeyDown,
    ...others
  },
  ref,
) {
  const showChevron = withChevron ?? !onRemove;
  // The Sales token always reads as applied, so `active` only styles "outline".
  const isActive = variant === "outline" && active;
  // The Sales token keeps its original non-inherit Text (Mantine md type);
  // the Reports outline chip inherits the chip's dense type.
  const inherit = variant === "outline";
  const classes = [
    styles.chip,
    styles[variant],
    isActive ? styles.active : "",
    onRemove ? styles.withRemove : "",
    className ?? "",
  ]
    .filter(Boolean)
    .join(" ");
  const rootStyle =
    valueMaxWidth === undefined
      ? style
      : ({
          ...style,
          "--filter-chip-value-max-width":
            typeof valueMaxWidth === "number"
              ? `${valueMaxWidth}px`
              : valueMaxWidth,
        } as React.CSSProperties);

  const content = (
    <>
      {icon && (
        <Box component="span" className={styles.icon}>
          {icon}
        </Box>
      )}
      {label !== undefined && label !== null && (
        <Text component="span" inherit={inherit} className={styles.label}>
          {label}
        </Text>
      )}
      {value !== undefined && value !== null && (
        <Text
          component="span"
          inherit={inherit}
          className={
            valueMaxWidth === undefined
              ? styles.value
              : `${styles.value} ${styles.valueTruncate}`
          }
        >
          {value}
        </Text>
      )}
      {hint !== undefined && hint !== null && (
        <Text component="span" inherit={inherit} className={styles.hint}>
          {hint}
        </Text>
      )}
      {showChevron && (
        <IconChevronDown
          size={12}
          stroke={2.4}
          className={styles.chevron}
          aria-hidden
        />
      )}
    </>
  );

  if (onRemove) {
    return (
      <Box
        ref={ref as React.Ref<HTMLDivElement>}
        role="button"
        tabIndex={0}
        className={classes}
        style={rootStyle}
        data-active={isActive || undefined}
        onClick={onClick}
        onKeyDown={(e: React.KeyboardEvent<HTMLElement>) => {
          onKeyDown?.(e);
          if (e.defaultPrevented) return;
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onClick?.(e as unknown as React.MouseEvent<HTMLElement>);
          }
        }}
        {...others}
      >
        {content}
        <UnstyledButton
          type="button"
          className={styles.remove}
          aria-label={removeLabel ?? "Remove filter"}
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            onRemove();
          }}
          onKeyDown={(e: React.KeyboardEvent) => e.stopPropagation()}
        >
          <IconX size={12} stroke={2.6} aria-hidden />
        </UnstyledButton>
      </Box>
    );
  }

  return (
    <UnstyledButton
      ref={ref as React.Ref<HTMLButtonElement>}
      type="button"
      className={classes}
      style={rootStyle}
      data-active={isActive || undefined}
      onClick={onClick}
      onKeyDown={onKeyDown}
      {...others}
    >
      {content}
    </UnstyledButton>
  );
});

export default FilterChip;
